import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Error from '../components/error';
import Feedback from '../components/feedback';
import LoadImage from '../components/load-image';
import { Report } from '../components/report';
import useEndTest from '../hooks/use-end-test';
import useInitializationObject from '../hooks/use-initialization-object';
import useLoadLearnosityItemsSDK from '../hooks/use-load-learnosity-items-sdk';
import EndOfLevelUrlObject from '../interfaces/end-of-level-url';
import './end-of-level.css';
import { Language } from '../interfaces/language.type';
import { ResultTab } from '../interfaces/result-tab';
import { availableLanguages } from '../localization/i18n';
import { LocalizationProvider } from '../localization/localization.provider';

function EndOfLevel() {
  const location = useLocation();
  const urlParameters = queryString.parse(location.search) as unknown as EndOfLevelUrlObject;
  const language = useMemo(() => {
    const lang = (urlParameters.lang as Language) || Language.EN;
    if (availableLanguages.includes(lang)) {
      return lang;
    }

    return Language.EN;
  }, [urlParameters.lang]);
  const [activeResultTab, setActiveResultTab] = useState<ResultTab>('feedback');
  const {
    loading: loadingInitializationObject,
    getItemsInitializationObject,
    itemsInitializationObject,
  } = useInitializationObject();
  const {
    loading: sendingResults,
    confirmTestFinalizationGetResults,
    endTestResult,
    testSubmitted,
    endTestResultError,
  } = useEndTest();
  const {
    loading: loadingLearnositySDK,
    getLearnositySDK,
    learnosityTestSubmitted,
    loadLearnosityTestError,
  } = useLoadLearnosityItemsSDK();

  useEffect(() => {
    if (itemsInitializationObject.loaded !== null || loadingInitializationObject) {
      return;
    }
    getItemsInitializationObject(urlParameters);
  }, [
    urlParameters,
    getItemsInitializationObject,
    itemsInitializationObject.loaded,
    loadingInitializationObject,
  ]);

  useEffect(() => {
    if (!itemsInitializationObject?.result) {
      return;
    }
    getLearnositySDK(itemsInitializationObject.result);
  }, [itemsInitializationObject.result, getLearnositySDK]);

  useEffect(() => {
    if (learnosityTestSubmitted && !sendingResults && !testSubmitted) {
      confirmTestFinalizationGetResults(itemsInitializationObject, urlParameters);
    }
  }, [
    confirmTestFinalizationGetResults,
    itemsInitializationObject,
    learnosityTestSubmitted,
    testSubmitted,
    sendingResults,
    urlParameters,
  ]);

  const loading = loadingInitializationObject || loadingLearnositySDK || sendingResults;

  const TestStateHandler = () => {
    if (
      itemsInitializationObject?.loaded === false ||
      endTestResultError ||
      loadLearnosityTestError
    ) {
      return <Error returnUrl={urlParameters.returnUrl} />;
    } else if (endTestResult?.status === 'ok' || endTestResult?.status === 'nok') {
      return (
        <>
          {activeResultTab === 'feedback' && (
            <Feedback endTestResult={{ ...endTestResult }} onSetActiveTab={setActiveResultTab} />
          )}
          {activeResultTab === 'report' && (
            <Report
              sessionId={itemsInitializationObject.result?.sessionId}
              endTestResult={endTestResult}
              onSetActiveTab={setActiveResultTab}
            />
          )}
        </>
      );
    } else if (loading) {
      return <LoadImage key="load" />;
    }
    return null;
  };

  return (
    <div className="app">
      <div className="main-block">
        <LocalizationProvider language={language}>
          <TestStateHandler />
        </LocalizationProvider>
        <div
          key="sdk"
          className="learnosity-wrapper"
          style={{ visibility: loading ? 'hidden' : 'visible' }}
          hidden={endTestResult?.status === 'ok'}
        >
          <div id="learnosity_assess" />
        </div>
      </div>
    </div>
  );
}

export default EndOfLevel;
